// Settings → Email & SMS: provider presets and the OTP-template rules the
// screen previews. The server re-checks all of it (vc_be
// services/instituteMailConfig.js, services/instituteSmsConfig.js,
// utils/smsProviders.js) — this file only makes the form helpful.

/** Mail providers. Picking one fills host/port/security; everything stays editable. */
export const MAIL_PRESETS = [
  {
    value: "gmail",
    label: "Google Workspace / Gmail",
    host: "smtp.gmail.com",
    port: 587,
    secure: false,
    hint: "Use an App Password (Google Account → Security → App passwords), not your normal password. 2-Step Verification must be on.",
  },
  {
    value: "outlook",
    label: "Microsoft 365 / Outlook",
    host: "smtp.office365.com",
    port: 587,
    secure: false,
    hint: "SMTP AUTH must be enabled for this mailbox in the Microsoft 365 admin centre. With MFA on, use an app password.",
  },
  {
    value: "zoho",
    label: "Zoho Mail",
    host: "smtp.zoho.in",
    port: 465,
    secure: true,
    hint: "smtp.zoho.in for accounts in India, smtp.zoho.com elsewhere. With 2FA on, generate an application-specific password.",
  },
  {
    value: "ses",
    label: "Amazon SES",
    host: "email-smtp.ap-south-1.amazonaws.com",
    port: 587,
    secure: false,
    hint: "Use SES SMTP credentials (not your AWS keys), and match the host to your SES region. The sender domain must be verified in SES.",
  },
  {
    value: "sendgrid",
    label: "SendGrid",
    host: "smtp.sendgrid.net",
    port: 587,
    secure: false,
    hint: 'The username is literally "apikey" and the password is your SendGrid API key. The sender must be verified in SendGrid.',
  },
  {
    value: "brevo",
    label: "Brevo (Sendinblue)",
    host: "smtp-relay.brevo.com",
    port: 587,
    secure: false,
    hint: "Use the SMTP login and key from Brevo → SMTP & API. The sender must be verified in Brevo.",
  },
  { value: "custom", label: "Other SMTP server", hint: "Your mail provider's SMTP settings." },
];

export const MAIL_SECURITY = [
  { value: "starttls", label: "STARTTLS (port 587)" },
  { value: "ssl", label: "SSL/TLS (port 465)" },
];

export const SMS_PROVIDERS = [
  { value: "smsgatewayhub", label: "SMSGatewayHub" },
  { value: "msg91", label: "MSG91" },
  { value: "twilio", label: "Twilio" },
  { value: "custom", label: "Other SMS API (custom HTTP)" },
];

/** What a {#var#} slot can be filled with. Keys match vc_be SLOT_VALUES. */
export const SLOT_OPTIONS = [
  { value: "otp", label: "The OTP code" },
  { value: "minutes", label: "Validity in minutes" },
  { value: "institute", label: "Your institute name" },
  { value: "purpose", label: "What it is for (e.g. login)" },
];

export const CUSTOM_PLACEHOLDERS = [
  ["{{mobile}}", "number without country code"],
  ["{{to}}", "number with country code, digits only"],
  ["{{to_plus}}", "number as +91…"],
  ["{{message}}", "the finished OTP message"],
  ["{{otp}}", "the code alone"],
  ["{{minutes}}", "validity"],
  ["{{templateId}}", "DLT template ID"],
  ["{{senderId}}", "sender ID"],
  ["{{entityId}}", "DLT entity ID"],
  ["{{apiKey}}", "API key (stored encrypted)"],
  ["{{apiSecret}}", "API secret (stored encrypted)"],
];

const DLT_SLOT = /\{#var#\}/g;

export const countSlots = (body) => (String(body || "").match(DLT_SLOT) || []).length;

/** Same rule as the server: a single-slot template can only mean the OTP. */
export function effectiveSlots(body, slots) {
  if (Array.isArray(slots) && slots.some(Boolean)) return slots;
  return countSlots(body) === 1 ? ["otp"] : [];
}

export const SAMPLE_VARS = { otp: "482916", minutes: "10", institute: "", purpose: "login" };

/** The message a student would receive, filled with sample values. */
export function previewOtp(body, slots, institute) {
  const vars = { ...SAMPLE_VARS, institute: institute || "Your Institute" };
  const map = effectiveSlots(body, slots);
  let i = 0;
  return String(body || "")
    .replace(DLT_SLOT, () => {
      const key = map[i++];
      return key ? vars[key] : "▢";
    })
    .replace(/\{(otp|minutes|institute|purpose)\}/g, (_, k) => vars[k]);
}

/** Does the template put the code in the message? */
export function carriesOtp(body, slots) {
  if (/\{otp\}/.test(String(body || ""))) return true;
  return effectiveSlots(body, slots).includes("otp");
}

/** Slot list resized to the template, keeping choices already made. */
export function fitSlots(body, slots) {
  const n = countSlots(body);
  const current = Array.isArray(slots) ? slots : [];
  return Array.from({ length: n }, (_, i) => current[i] || (n === 1 ? "otp" : ""));
}

/** "2 minutes ago" style for health timestamps. */
export function timeAgo(value) {
  if (!value) return "";
  const s = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
